import { atom } from 'jotai'
import {
	atomWithReset,
	atomWithStorage,
	createJSONStorage,
} from 'jotai/utils'

import { primaryColorAtom } from '@/content-from-sections/client'
import { getStore } from '@/store'
import { getContrastingNeutralColor } from '@/utils'

import { QR_TYPE } from './generatorTypes'
import { QR_TYPE_TO_CONTENT_ATOM } from './utils/qrTypeToContentAtom'

export const qrTypeAtom = atom<QR_TYPE | null>(null)
export const altLandingQrTypeAtom = atom<QR_TYPE | null>(null)

// Tracks which QR_TYPE the Step 2 content atoms currently hold, independent of
// qrTypeAtom (which gets nulled on every Step 1 mount for hover-preview UX).
// Used to decide whether reselecting a type on Step 1 should reset Step 2 content.
export const activeContentTypeAtom = atom<QR_TYPE | null>(null)

export const hasLandedFromAltLandingAtom = atomWithStorage<boolean>(
	'hasLandedFromAltLanding',
	false,
	createJSONStorage(() => sessionStorage),
)

export const isPostSignUpVisitAtom = atom(false)

export const ONBOARDING_QR_ID_LS = 'onboarding-qr-id'

const setTabScopedItem = (key: string, value: string) => {
	sessionStorage.setItem(key, value)
	localStorage.setItem(key, value)
}

const getTabScopedItem = (key: string) =>
	sessionStorage.getItem(key) ?? localStorage.getItem(key)

const removeTabScopedItem = (key: string) => {
	sessionStorage.removeItem(key)
	localStorage.removeItem(key)
}

export const setOnboardingQrId = (qrId: string) =>
	setTabScopedItem(ONBOARDING_QR_ID_LS, qrId)

export const getOnboardingQrId = () => getTabScopedItem(ONBOARDING_QR_ID_LS)

export const clearOnboardingQrId = () =>
	removeTabScopedItem(ONBOARDING_QR_ID_LS)

// MYQRCODE-10778 AC#13: generated once at builder_funnel_start, then read
// (not regenerated) by every later builder/signup-modal/checkout event so a
// purchase can be traced back to the QR that produced it.
export const BUILDER_SESSION_ID_LS = 'builder-session-id'
const BUILDER_SESSION_STARTED_AT_LS = 'builder-session-started-at'

export const startNewBuilderSession = (): string => {
	const builderSessionId =
		globalThis.crypto?.randomUUID?.() || Math.random().toString(36).slice(2)
	setTabScopedItem(BUILDER_SESSION_ID_LS, builderSessionId)
	setTabScopedItem(BUILDER_SESSION_STARTED_AT_LS, String(Date.now()))
	return builderSessionId
}

export const getBuilderSessionId = (): string | null =>
	getTabScopedItem(BUILDER_SESSION_ID_LS)

export const getBuilderSessionDurationSec = (): number | undefined => {
	const startedAt = getTabScopedItem(BUILDER_SESSION_STARTED_AT_LS)
	if (!startedAt) return undefined
	return Math.round((Date.now() - Number(startedAt)) / 1000)
}

export const qrUriAtom = atom<string | null>(null)

export const contentFormValidityAtom = atomWithReset<Record<string, boolean>>(
	{},
)

export const isPreviewDialogOpenAtom = atom(false)

export const isSignUpDialogOpenAtom = atom(false)
export const signUpDefaultEmailAtom = atomWithStorage<{
	qrId: string
	previousEmail?: string
	draftEmail?: string
} | null>('onboarding-signup-email', null)

export const isValidationErrorDialogOpenAtom = atom(false)

export const openOverlayIdAtom = atom<string | null>(null)

export const isOverlayOpenAtom = atom((get) => !!get(openOverlayIdAtom))

export const previewModeAtom = atom<'preview' | 'qr'>('preview')

export const isDemoModeAtom = atom(false)

// Tracks whether the active content type has genuinely been edited by the
// user, independent of isDemoModeAtom (which GeneratorPreview forces true
// while Step 1 is active, for the type-card hover preview, regardless of
// whether the content behind it was ever touched). Reset whenever the active
// type changes; set by markContentTouched wherever a field is first edited.
export const isContentTouchedAtom = atom(false)

export const markContentTouched = () => {
	getStore().set(isDemoModeAtom, false)
	getStore().set(isContentTouchedAtom, true)
}

export type TPreviewStatusBarColor = 'black' | 'white'

export const previewStatusBarColorAtom = atom<TPreviewStatusBarColor>((get) => {
	const isOverlayOpen = get(isOverlayOpenAtom)
	const previewMode = get(previewModeAtom)
	const qrType = get(qrTypeAtom)
	const primaryColor = get(primaryColorAtom)

	if (
		isOverlayOpen ||
		previewMode === 'qr' ||
		!qrType ||
		qrType === QR_TYPE.PlainText ||
		qrType === QR_TYPE.Url ||
		qrType === QR_TYPE.Facebook
	) {
		return 'black'
	}

	return getContrastingNeutralColor(primaryColor)
})

export const qrContentAtom = atom((get) => {
	const qrType = get(qrTypeAtom)
	if (!qrType) {
		return null
	}
	const qrContentAtom = QR_TYPE_TO_CONTENT_ATOM[qrType]
	return get(qrContentAtom)
})
